import { DeleteOutlined, EditOutlined, PlusOutlined, ArrowLeftOutlined, PlayCircleOutlined, HistoryOutlined, PoweroffOutlined, StopOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, DatePicker, Drawer, Form, Input, InputNumber, Modal, Pagination, Row, Select, Space, Spin, Table, Tabs, Tag, message } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { cancelPublishTaskRun, executePublishTaskOnce, getActivePublishTaskRuns, getPublishTaskList, triggerPublishTaskOnce, updatePublishTask, deletePublishTask, searchPublishAccounts, nlToSchedule, createPublishTask } from '../api';
import ProducerSchedulesPanel from '../components/ProducerSchedulesPanel';
import PublishRunViewer from '../components/PublishRunViewer';
import PublishRunHistoryDrawer from '../components/PublishRunHistoryDrawer';
import XhsAccountCheckButton from '../components/XhsAccountCheckButton';
import DouyinAccountCheckButton from '../components/DouyinAccountCheckButton';
import KuaishouAccountCheckButton from '../components/KuaishouAccountCheckButton';
import { useAuth } from '../auth/AuthContext';
import { resolveErrorMessage } from '../utils/errorMessage';

const { RangePicker } = DatePicker;

const parseMainTabFromSearch = (search) => {
  const tab = new URLSearchParams(search || '').get('tab');
  return tab === 'produce' ? 'produce' : 'publish';
};

const TimedPublish = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [detailForm] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [accountFilterForm] = Form.useForm();
  const [publishTimeForm] = Form.useForm();
  const { user } = useAuth();
  const [dataSource, setDataSource] = useState([]);
  const [selectedTaskKeys, setSelectedTaskKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeRunsByTaskId, setActiveRunsByTaskId] = useState({});
  const [publishRunViewerOpen, setPublishRunViewerOpen] = useState(false);
  const [activeTaskRunId, setActiveTaskRunId] = useState('');
  const [publishRunHistoryOpen, setPublishRunHistoryOpen] = useState(false);
  const [historyTask, setHistoryTask] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [createTaskDrawerVisible, setCreateTaskDrawerVisible] = useState(false);
  const [publishAccountDrawerVisible, setPublishAccountDrawerVisible] = useState(false);
  const [publishTimeDrawerVisible, setPublishTimeDrawerVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  // 发布账号抽屉相关状态
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountDataSource, setAccountDataSource] = useState([]);
  const [accountPagination, setAccountPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [targetAccountLoading, setTargetAccountLoading] = useState(false);
  const [targetAccountOptions, setTargetAccountOptions] = useState([]);
  const targetAccountNameMapRef = useRef(new Map());
  const targetAccountReqIdRef = useRef(0);
  const lastTargetPlatformsKeyRef = useRef('');
  const [selectedAccountKeys, setSelectedAccountKeys] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const selectedAccountMapRef = useRef(new Map());
  const [selectedFilters, setSelectedFilters] = useState(null);
  // 发布时间解析相关状态
  const [parsingSchedule, setParsingSchedule] = useState(false);
  const [scheduleParseResult, setScheduleParseResult] = useState(null);
  const recentlyDeletedTaskIdsRef = useRef(new Map());
  const [taskAccountMap, setTaskAccountMap] = useState({});
  const [taskAccountLoading, setTaskAccountLoading] = useState(false);
  const taskAccountReqIdRef = useRef(0);

  const mainTabKey = useMemo(() => parseMainTabFromSearch(location.search), [location.search]);
  const [mountedMainTabs, setMountedMainTabs] = useState(() => ({
    publish: mainTabKey === 'publish',
    produce: mainTabKey === 'produce',
  }));

  // 点击左侧“任务管理”默认进入“发布任务”
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const tab = params.get('tab');
    if (tab !== 'publish' && tab !== 'produce') {
      params.set('tab', 'publish');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  // 懒挂载：首次切换到某个主 Tab 时才挂载，之后切换保留内部筛选/分页/勾选状态
  useEffect(() => {
    setMountedMainTabs((prev) => ({ ...prev, [mainTabKey]: true }));
  }, [mainTabKey]);

  // 状态映射
  const statusMap = {
    'active': { text: '运行中', color: 'green' },
    'paused': { text: '已停用', color: 'orange' },
    'completed': { text: '已完成', color: 'blue' },
    'failed': { text: '失败', color: 'red' },
  };

  const runStatusMap = {
    pending: { text: 'pending', color: 'gold' },
    running: { text: 'running', color: 'gold' },
    success: { text: 'success', color: 'green' },
    failed: { text: 'failed', color: 'red' },
    noop: { text: 'noop', color: 'blue' },
    canceled: { text: 'canceled', color: 'default' },
  };

  // 任务类型映射
  const taskTypeMap = {
    'cron': '定时任务',
    'once': '单次任务',
    'interval': '间隔任务',
    'manual': '手动任务',
  };

  const executionModeMap = {
    local_agent: { text: '本地 Agent', color: 'processing' },
    cloud_api: { text: '云端 API', color: 'blue' },
    cloud_browser: { text: '云端浏览器', color: 'purple' },
  };
  const localAgentPlatforms = new Set(['douyin', 'kuaishou', 'weixin_channels']);

  // 格式化时间戳
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      const date = new Date(dateTimeString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return dateTimeString;
    }
  };

  const formatListField = (value) => {
    if (value === null || value === undefined) return '';
    const items = Array.isArray(value) ? value : [value];
    const text = items
      .filter((item) => item !== null && item !== undefined)
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join(', ');
    return text;
  };

  const normalizeList = (value) => {
    if (value === null || value === undefined) return [];
    if (Array.isArray(value)) return value.filter((item) => item !== null && item !== undefined).map((item) => String(item).trim()).filter(Boolean);
    return String(value).split(',').map((item) => String(item).trim()).filter(Boolean);
  };

  const buildAccountAgentView = (account) => {
    const readiness = account?.login_readiness && typeof account.login_readiness === 'object' ? account.login_readiness : {};
    const agentSummary = account?.agent_summary && typeof account.agent_summary === 'object' ? account.agent_summary : {};
    const platformCode = String(account?.platform_code || '').trim();
    const executionMode = String(account?.execution_mode || readiness.execution_mode || '').trim();
    const authState = String(account?.auth_state || readiness.auth_state || '').trim();
    const agentRequired = readiness.agent_required === true
      || executionMode === 'local_agent'
      || localAgentPlatforms.has(platformCode);
    const agentBound = readiness.agent_bound === true || Boolean(account?.agent_id);
    const hasOnlineFlag = typeof readiness.agent_online === 'boolean' || typeof agentSummary?.is_online === 'boolean';
    const agentOnline = typeof readiness.agent_online === 'boolean'
      ? readiness.agent_online
      : (typeof agentSummary?.is_online === 'boolean'
        ? agentSummary.is_online
        : String(agentSummary?.status || '').toLowerCase() === 'online');
    const canPublish = readiness.can_publish === true;
    const canLoginViaAgent = readiness.can_login_via_agent === true;
    const statusText = String(readiness.status_text || '').trim();
    const messageText = String(readiness.message || '').trim();

    if (!agentRequired) {
      return {
        state: 'cloud',
        tagText: '无需 Agent',
        tagColor: 'blue',
        summary: statusText || '云端链路',
        detail: messageText,
        executionMode,
        authState,
        canLoginViaAgent,
      };
    }
    if (!agentBound) {
      return {
        state: 'unbound',
        tagText: '未绑定',
        tagColor: 'orange',
        summary: statusText || '需绑定 Agent',
        detail: messageText,
        executionMode,
        authState,
        canLoginViaAgent,
      };
    }
    if (hasOnlineFlag && !agentOnline) {
      return {
        state: 'offline',
        tagText: '离线',
        tagColor: 'red',
        summary: statusText || 'Agent 离线',
        detail: messageText,
        executionMode,
        authState,
        canLoginViaAgent,
      };
    }
    if (canPublish) {
      return {
        state: 'ready',
        tagText: '就绪',
        tagColor: 'green',
        summary: statusText || '可发布',
        detail: messageText,
        executionMode,
        authState,
        canLoginViaAgent,
      };
    }
    return {
      state: 'needs_login',
      tagText: '待登录',
      tagColor: 'gold',
      summary: statusText || '需重新登录',
      detail: messageText,
      executionMode,
      authState,
      canLoginViaAgent,
    };
  };

  const summarizeTaskAgentStatus = (task) => {
    const accountIds = normalizeList(task?.target_account_ids);
    if (!accountIds.length) {
      return {
        tagText: '未配置账号',
        tagColor: 'default',
        detail: '请先配置目标账号',
        level: 'none',
      };
    }

    let localRequired = 0;
    let ready = 0;
    let unbound = 0;
    let offline = 0;
    let needsLogin = 0;
    let unknown = 0;

    accountIds.forEach((accountId) => {
      const account = taskAccountMap?.[accountId];
      if (!account) {
        unknown += 1;
        return;
      }
      const view = buildAccountAgentView(account);
      if (view.state === 'cloud') return;
      localRequired += 1;
      if (view.state === 'ready') {
        ready += 1;
      } else if (view.state === 'unbound') {
        unbound += 1;
      } else if (view.state === 'offline') {
        offline += 1;
      } else {
        needsLogin += 1;
      }
    });

    if (localRequired === 0) {
      return {
        tagText: '云端执行',
        tagColor: 'blue',
        detail: '目标账号不依赖本地 Agent',
        level: 'ok',
      };
    }

    if (!taskAccountLoading && unknown === 0 && unbound === 0 && offline === 0 && needsLogin === 0) {
      return {
        tagText: 'Agent 就绪',
        tagColor: 'green',
        detail: `依赖 Agent 账号 ${localRequired} 个，均可发布`,
        level: 'ok',
      };
    }

    const riskParts = [];
    if (unbound) riskParts.push(`未绑定 ${unbound}`);
    if (offline) riskParts.push(`离线 ${offline}`);
    if (needsLogin) riskParts.push(`待登录 ${needsLogin}`);
    if (unknown) riskParts.push(`待加载 ${unknown}`);
    return {
      tagText: '有风险',
      tagColor: offline > 0 || unbound > 0 ? 'red' : 'orange',
      detail: `依赖 Agent 账号 ${localRequired} 个，${riskParts.join(' / ')}`,
      level: 'risk',
      readyCount: ready,
      localRequired,
    };
  };

  const watchedTargetPlatforms = Form.useWatch('target_platforms', detailForm);
  const watchedTargetAccountIds = Form.useWatch('target_account_ids', detailForm);
  const targetPlatformKey = useMemo(
    () => normalizeList(watchedTargetPlatforms).sort().join('|'),
    [watchedTargetPlatforms]
  );
  const selectedTargetIds = useMemo(
    () => normalizeList(watchedTargetAccountIds),
    [watchedTargetAccountIds]
  );
  const hasTargetPlatforms = Boolean(targetPlatformKey);

  const buildTargetAccountOptions = (accounts = [], selectedIds = []) => {
    const options = [];
    const seen = new Set();
    (accounts || []).forEach((account) => {
      const accountId = account?.account_id;
      if (!accountId) return;
      const accountName = String(account?.account_name || '').trim();
      if (accountName) {
        targetAccountNameMapRef.current.set(accountId, accountName);
      }
      const label = accountName || accountId;
      const canOperate = account?.permission?.can_operate !== false;
      options.push({
        value: accountId,
        label: canOperate ? label : `${label}（仅可查看）`,
        title: accountName ? `${accountName} (${accountId})` : accountId,
        disabled: !canOperate,
      });
      seen.add(accountId);
    });

    (selectedIds || []).forEach((accountId) => {
      const id = String(accountId || '').trim();
      if (!id || seen.has(id)) return;
      const accountName = targetAccountNameMapRef.current.get(id) || '';
      const label = accountName || id;
      options.push({
        value: id,
        label: `${label}（权限未知）`,
        title: accountName ? `${accountName} (${id})` : id,
        disabled: true,
      });
      seen.add(id);
    });

    return options;
  };

  // 构建筛选参数
  const buildSearchParams = (formValues = {}, page = 1, pageSize = 20) => {
    const params = {
      page,
      page_size: pageSize,
      keyword: formValues.keyword || '',
      filters: {},
      sort: { field: 'created_at', order: 'desc' },
    };

    // 处理创建者筛选
    if (formValues.creator_user_id && formValues.creator_user_id.trim()) {
      params.filters.creator_user_id = formValues.creator_user_id.trim();
    }
    if (formValues.creator_user_name && formValues.creator_user_name.trim()) {
      params.filters.creator_user_name = formValues.creator_user_name.trim();
    }

    // 处理状态筛选
    if (formValues.status && formValues.status.length > 0) {
      params.filters.status = formValues.status;
    }

    // 处理任务类型筛选
    if (formValues.task_type && formValues.task_type.length > 0) {
      params.filters.task_type = formValues.task_type;
    }

    // 如果 filters 为空对象，可以删除它
    if (Object.keys(params.filters).length === 0) {
      delete params.filters;
    }

    return params;
  };

  const markTasksDeleted = (taskIds = []) => {
    const now = Date.now();
    const ids = Array.from(new Set((taskIds || []).filter(Boolean)));
    ids.forEach((id) => recentlyDeletedTaskIdsRef.current.set(id, now));
  };

  const filterRecentlyDeletedTasks = (items = []) => {
    const ttlMs = 8000;
    const now = Date.now();
    const deletedMap = recentlyDeletedTaskIdsRef.current;

    // 清理过期记录
    for (const [id, ts] of deletedMap.entries()) {
      if (!ts || now - ts > ttlMs) deletedMap.delete(id);
    }

    if (deletedMap.size === 0) return items || [];
    return (items || []).filter((item) => !deletedMap.has(item?.task_id));
  };

  const loadTaskAccountSnapshots = async (tasks = []) => {
    const accountIds = Array.from(new Set(
      (tasks || [])
        .flatMap((task) => normalizeList(task?.target_account_ids))
        .filter(Boolean),
    ));
    if (accountIds.length === 0) {
      setTaskAccountMap({});
      return;
    }

    const reqId = taskAccountReqIdRef.current + 1;
    taskAccountReqIdRef.current = reqId;
    setTaskAccountLoading(true);
    try {
      let page = 1;
      const pageSize = Math.min(100, Math.max(20, accountIds.length));
      const items = [];
      let total = 0;
      while (true) {
        const resp = await searchPublishAccounts({
          page,
          page_size: pageSize,
          keyword: '',
          filters: { account_ids: accountIds },
          sort: { field: 'created_at', order: 'desc' },
        });
        if (resp?.status !== 'success') {
          break;
        }
        if (reqId !== taskAccountReqIdRef.current) return;
        const pageItems = Array.isArray(resp?.items) ? resp.items : [];
        items.push(...pageItems);
        total = Number(resp?.total || 0);
        if (pageItems.length === 0 || items.length >= total) break;
        page += 1;
      }
      if (reqId !== taskAccountReqIdRef.current) return;
      const nextMap = {};
      items.forEach((item) => {
        if (!item?.account_id) return;
        nextMap[item.account_id] = item;
      });
      setTaskAccountMap(nextMap);
    } catch (error) {
      console.error('加载任务账号状态失败:', error);
      if (reqId === taskAccountReqIdRef.current) {
        setTaskAccountMap({});
      }
    } finally {
      if (reqId === taskAccountReqIdRef.current) {
        setTaskAccountLoading(false);
      }
    }
  };

  // 加载数据
  const loadData = async (searchParams = {}, page = null, pageSize = null) => {
    setLoading(true);
    try {
      const currentPage = page !== null ? page : pagination.current;
      const currentPageSize = pageSize !== null ? pageSize : pagination.pageSize;

      const params = {
        ...searchParams,
        page: currentPage,
        page_size: currentPageSize,
      };

      const response = await getPublishTaskList(params);

      if (response.status === 'success') {
        const filtered = filterRecentlyDeletedTasks(response.items || []);
        setDataSource(filtered);
        void loadTaskAccountSnapshots(filtered);
        setPagination({
          current: response.page || currentPage,
          pageSize: response.page_size || currentPageSize,
          total: response.total || 0,
        });
        try {
          const taskIds = (filtered || []).map((it) => it?.task_id).filter(Boolean);
          const ar = await getActivePublishTaskRuns({ task_ids: taskIds });
          if (ar?.status === 'success' && ar?.items && typeof ar.items === 'object') {
            setActiveRunsByTaskId(ar.items);
          } else {
            setActiveRunsByTaskId({});
          }
        } catch (e) {
          setActiveRunsByTaskId({});
        }
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 初始加载数据
  useEffect(() => {
    loadData({});
  }, []);

  const currentPageTaskIds = (dataSource || [])
    .map((item) => item.task_id)
    .filter(Boolean);

  const isAllCurrentPageSelected = currentPageTaskIds.length > 0
    && currentPageTaskIds.every((id) => selectedTaskKeys.includes(id));

  const toggleSelectAllCurrentPage = () => {
    if (currentPageTaskIds.length === 0) return;

    setSelectedTaskKeys((prevKeys) => {
      const otherPageSelected = (prevKeys || []).filter((key) => !currentPageTaskIds.includes(key));
      const prevAllCurrent = currentPageTaskIds.every((id) => (prevKeys || []).includes(id));
      if (prevAllCurrent) return otherPageSelected;
      return Array.from(new Set([...otherPageSelected, ...currentPageTaskIds]));
    });
  };

  // 处理检索
  const handleSearch = () => {
    const formValues = form.getFieldsValue();
    const searchParams = buildSearchParams(formValues, 1, pagination.pageSize);
    setSelectedTaskKeys([]);
    loadData(searchParams, 1, pagination.pageSize);
  };

  const reloadListAfterUpdate = async () => {
    const formValues = form.getFieldsValue();
    const searchParams = buildSearchParams(formValues, pagination.current, pagination.pageSize);
    await loadData(searchParams, pagination.current, pagination.pageSize);
  };

  const handleTriggerOnce = async (record) => {
    if (!record?.task_id) return;
    if (String(record?.status || '').toLowerCase() !== 'active') {
      message.warning('任务已停用/未启用，不能立即执行');
      return;
    }
    if (activeRunsByTaskId?.[record?.task_id]) {
      message.warning('任务正在运行，已阻止本次触发');
      return;
    }

    Modal.confirm({
      title: '立即执行',
      content: `将立即执行一次该发布任务（本次按“每账号 1 条”执行，不改变原定时规则）。确定要执行吗？`,
      okText: '立即执行',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await triggerPublishTaskOnce({ task_id: record.task_id });
          if (resp?.status === 'accepted') {
            message.success('已触发一次运行');
            if (resp?.task_run_id) {
              setActiveTaskRunId(resp.task_run_id);
              setPublishRunViewerOpen(true);
            }
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'conflict') {
            message.warning('任务正在运行，已阻止本次触发');
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'forbidden') {
            message.warning('任务已停用/未启用，不能立即执行');
            await reloadListAfterUpdate();
            return;
          }
          message.error(resolveErrorMessage(resp?.error_message, '触发失败'));
          await reloadListAfterUpdate();
        } catch (e) {
          message.error(e?.message || String(e));
          await reloadListAfterUpdate();
        }
      },
    });
  };

  const isManualTask = (record) => {
    const taskType = String(record?.task_type || '').toLowerCase();
    const taskId = String(record?.task_id || '');
    return taskType === 'manual' || taskId.startsWith('manual_');
  };

  const handleExecuteOnce = async (record) => {
    if (!record?.task_id) return;
    if (activeRunsByTaskId?.[record?.task_id]) {
      message.warning('任务正在运行，已阻止本次触发');
      return;
    }

    Modal.confirm({
      title: '立即执行',
      content: '将立即执行一次“手动批量发布”（单平台多账号，每账号 1 条）。确定要执行吗？',
      okText: '立即执行',
      cancelText: '取消',
      onOk: async () => {
        try {
          const platformCode = Array.isArray(record?.target_platform_codes) && record.target_platform_codes.length === 1
            ? record.target_platform_codes[0]
            : undefined;
          const resp = await executePublishTaskOnce({
            task_id: record.task_id,
            platform_code: platformCode,
            per_account_limit: 1,
          });
          if (resp?.status === 'accepted') {
            message.success('已触发一次执行');
            if (resp?.task_run_id) {
              setActiveTaskRunId(resp.task_run_id);
              setPublishRunViewerOpen(true);
            }
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'conflict') {
            message.warning('任务正在运行，已阻止本次触发');
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'forbidden') {
            message.warning('任务已停用/未启用，不能执行');
            await reloadListAfterUpdate();
            return;
          }
          message.error(resolveErrorMessage(resp?.error_message, '执行失败'));
          await reloadListAfterUpdate();
        } catch (e) {
          message.error(e?.message || String(e));
          await reloadListAfterUpdate();
        }
      },
    });
  };

  const openRunHistory = (record) => {
    setHistoryTask(record || null);
    setPublishRunHistoryOpen(true);
  };

  const handleCancelActiveRun = async (record) => {
    const taskId = record?.task_id;
    if (!taskId) return;
    const active = activeRunsByTaskId?.[taskId];
    const runId = active?.run_id;
    if (!runId) {
      message.warning('未找到运行中的实例');
      return;
    }

    Modal.confirm({
      title: '停止运行',
      content: '将停止后续发布作业（未执行的会标记为 canceled）。正在执行中的发布可能仍会继续。确定要停止吗？',
      okText: '停止运行',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await cancelPublishTaskRun(runId);
          if (resp?.status === 'success') {
            message.success('已停止后续作业');
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'ignored') {
            message.info('该运行已结束/不可停止');
            await reloadListAfterUpdate();
            return;
          }
          message.error(resolveErrorMessage(resp?.error_message, '停止失败'));
          await reloadListAfterUpdate();
        } catch (e) {
          message.error(e?.message || String(e));
          await reloadListAfterUpdate();
        }
      },
    });
  };

  const handleToggleTaskStatus = async (record) => {
    if (!record?.task_id) return;
    const current = String(record.status || '').toLowerCase();
    const next = current === 'active' ? 'paused' : 'active';
    const hasActiveRun = !!activeRunsByTaskId?.[record.task_id];

    Modal.confirm({
      title: next === 'paused' ? '停用任务' : '启用任务',
      content: next === 'paused' && hasActiveRun
        ? '当前任务存在运行中的实例；停用只会影响后续触发，不会中断当前运行。确定要停用吗？'
        : '确定要执行该操作吗？',
      okText: next === 'paused' ? '停用' : '启用',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await updatePublishTask({ task_id: record.task_id, status: next });
          if (resp?.status === 'success') {
            message.success(next === 'paused' ? '已停用' : '已启用');
            await reloadListAfterUpdate();
            return;
          }
          if (resp?.status === 'not_found') {
            message.error('任务不存在');
            await reloadListAfterUpdate();
            return;
          }
          message.error(resolveErrorMessage(resp?.error_message, '更新失败'));
          await reloadListAfterUpdate();
        } catch (e) {
          message.error(e?.message || String(e));
          await reloadListAfterUpdate();
        }
      },
    });
  };

  const reloadListAfterDelete = async (deletedTaskIds = []) => {
    const ids = Array.from(new Set((deletedTaskIds || []).filter(Boolean)));
    const deletedOnCurrentPage = ids.filter((id) => currentPageTaskIds.includes(id)).length;
    const shouldBackPage = pagination.current > 1 && currentPageTaskIds.length > 0 && deletedOnCurrentPage >= currentPageTaskIds.length;
    const nextPage = shouldBackPage ? pagination.current - 1 : pagination.current;

    const formValues = form.getFieldsValue();
    const searchParams = buildSearchParams(formValues, nextPage, pagination.pageSize);
    await loadData(searchParams, nextPage, pagination.pageSize);
  };

  // 处理筛选
  const handleFilterFinish = (values) => {
    const searchParams = buildSearchParams(values, 1, pagination.pageSize);
    setSelectedTaskKeys([]);
    loadData(searchParams, 1, pagination.pageSize);
  };

  // 处理编辑按钮点击
  const handleEdit = (record) => {
    const rawTriggerType = record.schedule?.trigger_type || 'cron';
    const normalizedTriggerType = rawTriggerType === 'fixed_time' ? 'once' : rawTriggerType;
    setCurrentTask(record);
    setDrawerVisible(true);
    setActiveTab('preview');
    setDetailLoading(false);
    const targetAccountIds = normalizeList(record.target_account_ids);
    const targetAccountNames = normalizeList(record.target_account_names);
    targetAccountNameMapRef.current.clear();
    targetAccountIds.forEach((id, idx) => {
      const name = targetAccountNames[idx];
      if (id && name) {
        targetAccountNameMapRef.current.set(id, name);
      }
    });
    setTargetAccountOptions(buildTargetAccountOptions([], targetAccountIds));
    
    // 设置表单初始值
    detailForm.setFieldsValue({
      task_name: record.task_name,
      status: record.status,
      max_items_per_account_per_run: record.max_items_per_account_per_run || 1,
      max_concurrency: record.max_concurrency ?? 3,
      trigger_type: normalizedTriggerType,
      cron_expression: record.schedule?.cron_expression || '',
      timezone: record.schedule?.timezone || 'Asia/Shanghai',
      run_at: record.schedule?.run_at ? dayjs(record.schedule.run_at) : null,
      interval_seconds: record.schedule?.interval_seconds ?? null,
      start_at: record.schedule?.start_at ? dayjs(record.schedule.start_at) : null,
      end_at: record.schedule?.end_at ? dayjs(record.schedule.end_at) : null,
      content_types: record.filters?.content_types || [],
      content_sources: record.filters?.content_sources || [],
      creators: record.filters?.creators || [],
      status_filter: record.filters?.status || [],
      risk_flag: record.filters?.risk_flag,
      is_manual_reviewed: record.filters?.is_manual_reviewed,
      primary_categories: record.filters?.primary_categories || [],
      secondary_categories: record.filters?.secondary_categories || [],
      platforms: record.filters?.platforms || [],
      target_platforms: record.target_platforms || [],
      target_account_ids: targetAccountIds,
    });
  };

  // 处理保存修改
  const handleSave = async () => {
    try {
      const values = await detailForm.validateFields();

      const toIsoMaybe = (value) => {
        if (!value) return null;
        if (typeof value === 'string') return value;
        if (typeof value.toISOString === 'function') return value.toISOString();
        return String(value);
      };
      
      const updateParams = {
        task_id: currentTask.task_id,
        task_name: values.task_name,
        status: values.status,
        max_items_per_account_per_run: values.max_items_per_account_per_run || 1,
        max_concurrency: values.max_concurrency,
        schedule: {
          trigger_type: values.trigger_type,
          timezone: values.timezone || 'Asia/Shanghai',
          cron_expression: values.trigger_type === 'cron' ? values.cron_expression : null,
          interval_seconds: values.trigger_type === 'interval' ? (values.interval_seconds ?? null) : null,
          start_at: values.trigger_type === 'interval' ? toIsoMaybe(values.start_at) : null,
          end_at: values.trigger_type === 'interval' ? toIsoMaybe(values.end_at) : null,
          run_at: values.trigger_type === 'once' ? toIsoMaybe(values.run_at) : null,
        },
        filters: {
          content_types: values.content_types || [],
          content_sources: values.content_sources || [],
          creators: values.creators || [],
          status: values.status_filter || [],
          risk_flag: values.risk_flag,
          is_manual_reviewed: values.is_manual_reviewed,
          primary_categories: values.primary_categories || [],
          secondary_categories: values.secondary_categories || [],
          platforms: values.platforms || [],
        },
        target_platforms: values.target_platforms || [],
        target_account_ids: values.target_account_ids || [],
      };

      const response = await updatePublishTask(updateParams);
      if (response.status === 'success') {
        message.success('更新成功');
        setDataSource((prev) => (prev || []).map((item) => (
          item?.task_id === updateParams.task_id
            ? {
              ...item,
              task_name: updateParams.task_name,
              status: updateParams.status,
              max_items_per_account_per_run: updateParams.max_items_per_account_per_run,
              schedule: updateParams.schedule,
              filters: updateParams.filters,
              target_platforms: updateParams.target_platforms,
              target_account_ids: updateParams.target_account_ids,
            }
            : item
        )));
        setCurrentTask((prev) => (prev?.task_id === updateParams.task_id ? { ...prev, ...updateParams } : prev));
        setDrawerVisible(false);
        await reloadListAfterUpdate();
      } else {
        message.error('更新失败');
      }
    } catch (error) {
      console.error('更新失败:', error);
      message.error('更新失败，请稍后重试');
    }
  };

  // 处理创建任务按钮点击
  const handleCreate = () => {
    setCreateTaskDrawerVisible(true);
    filterForm.resetFields();
  };

  // 处理筛选条件表单提交
  const handleFilterFormFinish = (values) => {
    // 保存筛选条件
    setSelectedFilters(values);
    // 关闭筛选条件抽屉，打开发布账号选择抽屉
    setCreateTaskDrawerVisible(false);
    setPublishAccountDrawerVisible(true);
    // 重置账号选择状态
    setSelectedAccountKeys([]);
    setSelectedAccounts([]);
    selectedAccountMapRef.current.clear();
    accountFilterForm.resetFields();
    // 加载发布账号列表
    loadPublishAccounts();
  };

  // 加载发布账号列表
  const loadPublishAccounts = async (params = {}) => {
    setAccountLoading(true);
    try {
      const page = params.page !== undefined ? params.page : accountPagination.current;
      const page_size = params.page_size !== undefined ? params.page_size : accountPagination.pageSize;

      // 获取表单筛选条件
      const filterValues = accountFilterForm.getFieldsValue();
      const filters = {};

      if (filterValues.platforms && filterValues.platforms.length > 0) {
        filters.platforms = Array.isArray(filterValues.platforms) ? filterValues.platforms : [filterValues.platforms];
      }
      if (filterValues.status && filterValues.status.length > 0) {
        filters.status = Array.isArray(filterValues.status) ? filterValues.status : [filterValues.status];
      }

      const requestParams = {
        page,
        page_size,
        keyword: params.keyword || '',
        filters: Object.keys(filters).length > 0 ? filters : (params.filters || {}),
        sort: params.sort || { field: 'created_at', order: 'desc' },
      };

      const data = await searchPublishAccounts(requestParams);

      if (data.status === 'success') {
        setAccountDataSource(data.items || []);
        setAccountPagination({
          current: data.page,
          pageSize: data.page_size,
          total: data.total,
        });
      } else {
        message.error('获取账号列表失败');
      }
    } catch (error) {
      console.error('搜索发布账号失败:', error);
      message.error('搜索发布账号失败，请稍后重试');
    } finally {
      setAccountLoading(false);
    }
  };

  const loadTargetAccounts = async (platforms = [], selectedIds = []) => {
    const platformFilters = normalizeList(platforms);
    if (platformFilters.length === 0) {
      setTargetAccountOptions(buildTargetAccountOptions([], selectedIds));
      return;
    }

    const reqId = targetAccountReqIdRef.current + 1;
    targetAccountReqIdRef.current = reqId;
    setTargetAccountLoading(true);
    try {
      let page = 1;
      const pageSize = 100;
      const items = [];
      let total = 0;

      while (true) {
        const resp = await searchPublishAccounts({
          page,
          page_size: pageSize,
          keyword: '',
          filters: { platforms: platformFilters },
          sort: { field: 'created_at', order: 'desc' },
        });
        if (resp?.status !== 'success') {
          message.error('获取账号列表失败');
          return;
        }
        if (reqId !== targetAccountReqIdRef.current) {
          return;
        }
        items.push(...(resp.items || []));
        total = resp.total || 0;
        if (items.length >= total || (resp.items || []).length === 0) break;
        page += 1;
      }

      const merged = buildTargetAccountOptions(items, selectedIds);
      setTargetAccountOptions(merged);
    } catch (error) {
      console.error('获取目标账号列表失败:', error);
      message.error('获取目标账号列表失败，请稍后重试');
    } finally {
      if (reqId === targetAccountReqIdRef.current) {
        setTargetAccountLoading(false);
      }
    }
  };

  // 处理账号筛选
  const handleAccountFilter = () => {
    // 重置到第一页
    setAccountPagination({
      ...accountPagination,
      current: 1,
    });
    loadPublishAccounts({ page: 1 });
  };

  const selectedAccountAgentRisk = (() => {
    if (!selectedAccountKeys.length) return null;
    let localRequired = 0;
    let ready = 0;
    let unbound = 0;
    let offline = 0;
    let needsLogin = 0;
    let unknown = 0;
    selectedAccountKeys.forEach((accountId) => {
      const account = selectedAccountMapRef.current.get(accountId);
      if (!account) {
        unknown += 1;
        return;
      }
      const view = buildAccountAgentView(account);
      if (view.state === 'cloud') return;
      localRequired += 1;
      if (view.state === 'ready') ready += 1;
      else if (view.state === 'unbound') unbound += 1;
      else if (view.state === 'offline') offline += 1;
      else needsLogin += 1;
    });
    return {
      total: selectedAccountKeys.length,
      localRequired,
      ready,
      unbound,
      offline,
      needsLogin,
      unknown,
    };
  })();

  useEffect(() => {
    if (!drawerVisible) return;
    if (lastTargetPlatformsKeyRef.current === targetPlatformKey) return;
    lastTargetPlatformsKeyRef.current = targetPlatformKey;
    const platforms = normalizeList(watchedTargetPlatforms);
    loadTargetAccounts(platforms, selectedTargetIds);
  }, [drawerVisible, targetPlatformKey, selectedTargetIds]);

  useEffect(() => {
    if (!drawerVisible) return;
    if (!selectedTargetIds.length) return;
    setTargetAccountOptions((prev) => {
      const options = Array.isArray(prev) ? [...prev] : [];
      const seen = new Set(options.map((opt) => opt.value));
      let changed = false;
      selectedTargetIds.forEach((id) => {
        const accountId = String(id || '').trim();
        if (!accountId || seen.has(accountId)) return;
        const accountName = targetAccountNameMapRef.current.get(accountId) || '';
        const label = accountName || accountId;
        options.push({
          value: accountId,
          label,
          title: accountName ? `${accountName} (${accountId})` : accountId,
        });
        seen.add(accountId);
        changed = true;
      });
      return changed ? options : prev;
    });
  }, [drawerVisible, selectedTargetIds]);

  useEffect(() => {
    if (drawerVisible) return;
    lastTargetPlatformsKeyRef.current = '';
  }, [drawerVisible]);

  // 处理返回筛选条件抽屉
  const handleBackToFilterSelection = () => {
    // 关闭发布账号选择抽屉
    setPublishAccountDrawerVisible(false);
    // 重新打开筛选条件抽屉
    setCreateTaskDrawerVisible(true);
    // 恢复之前选择的筛选条件到表单
    if (selectedFilters) {
      filterForm.setFieldsValue(selectedFilters);
    }
  };

  // 处理账号选择确认
  const handleConfirmAccountSelection = () => {
    if (selectedAccountKeys.length === 0) {
      message.warning('请至少选择一个发布账号');
      return;
    }
    // 根据选中的 keys 获取完整的账号信息（支持跨页选择）
    const selected = selectedAccountKeys
      .map((accountId) => selectedAccountMapRef.current.get(accountId))
      .filter(Boolean);
    const risk = selected.reduce((acc, account) => {
      const view = buildAccountAgentView(account);
      if (view.state === 'cloud') return acc;
      acc.localRequired += 1;
      if (view.state === 'ready') acc.ready += 1;
      else if (view.state === 'unbound') acc.unbound += 1;
      else if (view.state === 'offline') acc.offline += 1;
      else acc.needsLogin += 1;
      return acc;
    }, { localRequired: 0, ready: 0, unbound: 0, offline: 0, needsLogin: 0 });
    if (risk.localRequired > 0 && (risk.unbound > 0 || risk.offline > 0 || risk.needsLogin > 0)) {
      message.warning(`已选账号中存在 Agent 风险：未绑定 ${risk.unbound}，离线 ${risk.offline}，待登录 ${risk.needsLogin}。`);
    }
    setSelectedAccounts(selected);
    // 关闭发布账号选择抽屉，打开发布时间选择抽屉
    setPublishAccountDrawerVisible(false);
    setPublishTimeDrawerVisible(true);
    // 重置表单和解析结果
    publishTimeForm.resetFields();
    setScheduleParseResult(null);
  };

  // 处理返回发布账号选择抽屉
  const handleBackToAccountSelection = () => {
    setPublishTimeDrawerVisible(false);
    setPublishAccountDrawerVisible(true);
    publishTimeForm.resetFields();
    setScheduleParseResult(null);
  };

  // 解析定时任务文本
  const handleParseSchedule = async () => {
    try {
      const values = await publishTimeForm.validateFields(['schedule_text']);
      const text = values.schedule_text?.trim();
      
      if (!text) {
        message.warning('请输入发布时间文本');
        return;
      }

      setParsingSchedule(true);
      setScheduleParseResult(null);

      const response = await nlToSchedule({ text });

      if (response.status === 'success') {
        setScheduleParseResult({
          success: true,
          data: response,
        });
        message.success('解析成功');
      } else if (response.status === 'needs_clarification') {
        setScheduleParseResult({
          success: false,
          needsClarification: true,
          data: response,
        });
        message.warning('需要补充信息');
      } else {
        setScheduleParseResult({
          success: false,
          data: response,
        });
        message.error('解析失败');
      }
    } catch (error) {
      console.error('解析定时任务文本失败:', error);
      message.error('解析失败，请稍后重试');
      setScheduleParseResult({
        success: false,
        error: error.message,
      });
    } finally {
      setParsingSchedule(false);
    }
  };

  // 处理创建任务
  const handleCreateTask = async () => {
    try {
      // 验证表单
      const values = await publishTimeForm.validateFields(['task_name', 'schedule_text', 'max_items_per_account_per_run', 'max_concurrency']);
      
      // 检查是否有解析结果
      if (!scheduleParseResult || !scheduleParseResult.success) {
        message.warning('请先解析发布时间文本');
        return;
      }

      // 检查是否选择了发布账号
      if (!selectedAccounts || selectedAccounts.length === 0) {
        message.warning('请选择发布账号');
        return;
      }

      // 检查是否有筛选条件
      if (!selectedFilters) {
        message.warning('请选择筛选条件');
        return;
      }

      // 构建 filters 对象
      const filters = {};
      
      // 内容类型
      if (selectedFilters.content_type && selectedFilters.content_type.length > 0) {
        filters.content_types = Array.isArray(selectedFilters.content_type) 
          ? selectedFilters.content_type 
          : [selectedFilters.content_type];
      }
      
      // 内容来源
      if (selectedFilters.source) {
        filters.content_sources = Array.isArray(selectedFilters.source)
          ? selectedFilters.source
          : [selectedFilters.source];
      }
      
      // 创作者
      if (selectedFilters.creator_user_id) {
        filters.creators = [selectedFilters.creator_user_id];
      }
      
      // 内容状态
      if (selectedFilters.status && selectedFilters.status.length > 0) {
        filters.status = Array.isArray(selectedFilters.status)
          ? selectedFilters.status
          : [selectedFilters.status];
      }
      
      // 风险状态
      if (selectedFilters.risk_flag !== undefined && selectedFilters.risk_flag !== null) {
        filters.risk_flag = selectedFilters.risk_flag;
      }
      
      // 是否审核
      if (selectedFilters.is_manual_reviewed !== undefined && selectedFilters.is_manual_reviewed !== null) {
        filters.is_manual_reviewed = selectedFilters.is_manual_reviewed;
      }
      
      // 是否需要人工审核
      if (selectedFilters.manual_review_required !== undefined && selectedFilters.manual_review_required !== null) {
        filters.manual_review_required = selectedFilters.manual_review_required;
      }
      
      // 标签
      if (selectedFilters.tags) {
        const tagsArray = typeof selectedFilters.tags === 'string'
          ? selectedFilters.tags.split(',').map(t => t.trim()).filter(Boolean)
          : selectedFilters.tags;
        if (tagsArray.length > 0) {
          filters.tags = tagsArray;
        }
      }
      
      // 投递平台
      if (selectedFilters.available_platforms && selectedFilters.available_platforms.length > 0) {
        filters.available_platforms = Array.isArray(selectedFilters.available_platforms)
          ? selectedFilters.available_platforms
          : [selectedFilters.available_platforms];
      }
      
      // 标题
      if (selectedFilters.title) {
        filters.title = selectedFilters.title.trim();
      }
      
      // 关键词
      if (selectedFilters.keyword) {
        filters.keyword = selectedFilters.keyword.trim();
      }
      
      // 内容ID
      if (selectedFilters.content_id) {
        filters.content_id = selectedFilters.content_id.trim();
      }
      
      // 入库时间范围
      if (selectedFilters.ingested_at_range) {
        // 入库时间：DatePicker.RangePicker，转换为后端需要的 from/to 结构
        // dayjs 对象通过 toDate() 转换为 Date 对象，然后使用 toISOString()
        const fromDate = selectedFilters.ingested_at_range[0]?.toDate 
          ? selectedFilters.ingested_at_range[0].toDate() 
          : selectedFilters.ingested_at_range[0];
        const toDate = selectedFilters.ingested_at_range[1]?.toDate 
          ? selectedFilters.ingested_at_range[1].toDate() 
          : selectedFilters.ingested_at_range[1];
        filters.ingested_at_range = {
          from: fromDate.toISOString(),
          to: toDate.toISOString(),
        };
      }

      // 从选中的账号中提取平台和账号ID
      const forbiddenAccounts = (selectedAccounts || []).filter((account) => account?.permission?.can_operate === false);
      if (forbiddenAccounts.length > 0) {
        message.error('所选账号包含无操作权限账号，请重新选择');
        return;
      }
      const targetPlatforms = [...new Set(selectedAccounts.map(account => account.platform))];
      const targetAccountIds = selectedAccounts.map(account => account.account_id);

      const parsedSchedule = scheduleParseResult.data?.schedule || {};
      const parsedTriggerRaw = String(parsedSchedule.trigger_type || '').trim().toLowerCase();
      const parsedTrigger = parsedTriggerRaw === 'fixed_time' ? 'once' : parsedTriggerRaw;

      // 获取任务类型（用于列表筛选/展示）
      const taskType = parsedTrigger === 'cron'
        ? 'cron'
        : parsedTrigger === 'interval'
          ? 'interval'
          : 'once';

      // 构建请求参数
      const creatorUserId = user?.wechat?.openid || user?.user_id || 'aispire';
      const creatorUserName = user?.nickname || user?.wechat?.nickname || user?.name || null;
      const createParams = {
        task_name: values.task_name,
        task_type: taskType,
        creator_user_id: creatorUserId,
        creator_user_name: creatorUserName || undefined,
        status: 'active',
        max_items_per_account_per_run: values.max_items_per_account_per_run || 1,
        max_concurrency: values.max_concurrency || 2,
        schedule: {
          trigger_type: taskType,
          timezone: parsedSchedule.timezone || 'Asia/Shanghai',
          cron_expression: taskType === 'cron' ? (parsedSchedule.cron_expression || null) : null,
          interval_seconds: taskType === 'interval' ? (parsedSchedule.interval_seconds || null) : null,
          start_at: taskType === 'interval' ? (parsedSchedule.start_at || null) : null,
          end_at: taskType === 'interval' ? (parsedSchedule.end_at || null) : null,
          run_at: taskType === 'once' ? (parsedSchedule.run_at || null) : null,
        },
        filters: Object.keys(filters).length > 0 ? filters : undefined,
        target_platforms: targetPlatforms,
        target_account_ids: targetAccountIds,
      };

      // 调用创建接口
      const response = await createPublishTask(createParams);

      if (response.status === 'success') {
        message.success('创建发布任务成功');
        // 关闭所有抽屉
        setPublishTimeDrawerVisible(false);
        setPublishAccountDrawerVisible(false);
        setCreateTaskDrawerVisible(false);
        // 重置所有表单和状态
        publishTimeForm.resetFields();
        filterForm.resetFields();
        accountFilterForm.resetFields();
        setScheduleParseResult(null);
        setSelectedFilters(null);
        setSelectedAccounts([]);
        setSelectedAccountKeys([]);
        // 重新加载任务列表
        const formValues = form.getFieldsValue();
        const searchParams = buildSearchParams(formValues, 1, pagination.pageSize);
        loadData(searchParams, 1, pagination.pageSize);
      } else {
        message.error(resolveErrorMessage(response.message, '创建发布任务失败'));
      }
    } catch (error) {
      console.error('创建发布任务失败:', error);
      message.error('创建发布任务失败，请稍后重试');
    }
  };

  // 处理删除
  const handleDelete = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除任务 "${record.task_name}" 吗？此操作不可恢复。`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await deletePublishTask([record.task_id]);
          if (response.status === 'success') {
            message.success('删除成功');
            setSelectedTaskKeys((prev) => (prev || []).filter((id) => id !== record.task_id));
            markTasksDeleted([record.task_id]);
            setDataSource((prev) => (prev || []).filter((item) => item?.task_id !== record.task_id));
            // 给后端一点时间落库，避免“刚删完又被拉回来的错觉”
            await new Promise((r) => setTimeout(r, 300));
            await reloadListAfterDelete([record.task_id]);
          } else {
            message.error('删除失败');
            await reloadListAfterUpdate();
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
          await reloadListAfterUpdate();
        }
      },
    });
  };

  // 处理批量删除
  const handleBatchDelete = () => {
    if (!selectedTaskKeys.length) {
      message.warning('请先选择要删除的任务');
      return;
    }

    const taskIds = [...selectedTaskKeys];
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${taskIds.length} 个任务吗？此操作不可恢复。`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          message.loading({ content: `正在删除 ${taskIds.length} 个任务...`, key: 'batchDeleteTask' });
          const response = await deletePublishTask(taskIds);
          if (response.status === 'success') {
            message.success({ content: '删除成功', key: 'batchDeleteTask' });
            setSelectedTaskKeys([]);
            markTasksDeleted(taskIds);
            setDataSource((prev) => (prev || []).filter((item) => !taskIds.includes(item?.task_id)));
            await new Promise((r) => setTimeout(r, 300));
            await reloadListAfterDelete(taskIds);
          } else {
            message.error({ content: resolveErrorMessage(response.message, '删除失败'), key: 'batchDeleteTask' });
            await reloadListAfterUpdate();
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error({ content: '批量删除失败，请稍后重试', key: 'batchDeleteTask' });
          await reloadListAfterUpdate();
        }
      },
    });
  };

  const columns = [
    {
      title: '任务ID',
      dataIndex: 'task_id',
      key: 'task_id',
      width: 150,
    },
    {
      title: '任务名称',
      dataIndex: 'task_name',
      key: 'task_name',
      width: 250,
      render: (value) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.4 }}>
          {value || '-'}
        </div>
      ),
    },
    {
      title: '任务类型',
      dataIndex: 'task_type',
      key: 'task_type',
      width: 120,
      render: (type) => taskTypeMap[type] || type,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusInfo = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '创建者ID',
      dataIndex: 'creator_user_id',
      key: 'creator_user_id',
      width: 160,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-all' },
      }),
      render: (value) => {
        const text = value ? String(value).trim() : '';
        if (!text) return '-';
        return (
          <div title={text} style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
            {text}
          </div>
        );
      },
    },
    {
      title: '创建者',
      dataIndex: 'creator_user_name',
      key: 'creator_user_name',
      width: 140,
      ellipsis: true,
      render: (value, record) => {
        const text = value ? String(value) : '';
        if (!text) return '-';
        const title = record?.creator_user_id ? `${text} (${record.creator_user_id})` : text;
        return <span title={title}>{text}</span>;
      },
    },
    {
      title: '目标平台',
      dataIndex: 'target_platforms',
      key: 'target_platforms',
      width: 150,
      render: (platforms) => {
        if (!platforms || !Array.isArray(platforms)) return '-';
        return (
          <Space size={[0, 8]} wrap>
            {platforms.map((platform, index) => (
              <Tag key={index}>{platform}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '目标账号',
      dataIndex: 'target_account_names',
      key: 'target_account_names',
      width: 200,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-word' },
      }),
      render: (value) => {
        const text = formatListField(value);
        if (!text) return '';
        return (
          <div title={text} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {text}
          </div>
        );
      },
    },
    {
      title: 'Agent依赖',
      key: 'agent_readiness',
      width: 230,
      render: (_, record) => {
        const summary = summarizeTaskAgentStatus(record);
        return (
          <div>
            <Tag color={summary.tagColor}>{summary.tagText}</Tag>
            <div style={{ color: '#333', fontSize: 12, lineHeight: 1.4 }}>
              {summary.detail}
            </div>
          </div>
        );
      },
    },
    {
      title: '每账号上限',
      dataIndex: 'max_items_per_account_per_run',
      key: 'max_items_per_account_per_run',
      width: 120,
      render: (value) => (value ? Number(value) : 1),
    },
    {
      title: '账号管理员',
      dataIndex: 'operator_user_names',
      key: 'operator_user_names',
      width: 160,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-word' },
      }),
      render: (value) => {
        const text = formatListField(value);
        if (!text) return '';
        return (
          <div title={text} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {text}
          </div>
        );
      },
    },
    {
      title: '管理员ID',
      dataIndex: 'operator_user_ids',
      key: 'operator_user_ids',
      width: 160,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-all' },
      }),
      render: (value) => {
        const text = formatListField(value);
        if (!text) return '';
        return (
          <div title={text} style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
            {text}
          </div>
        );
      },
    },
    {
      title: '定时规则',
      key: 'schedule',
      width: 200,
      render: (_, record) => {
        if (record.schedule) {
          if (record.schedule.trigger_type === 'cron' && record.schedule.cron_expression) {
            return (
              <div>
                <div>Cron: {record.schedule.cron_expression}</div>
                {record.schedule.timezone && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    时区: {record.schedule.timezone}
                  </div>
                )}
              </div>
            );
          } else if (record.schedule.trigger_type === 'interval' && record.schedule.interval_seconds) {
            return (
              <div>
                <div>间隔: 每 {record.schedule.interval_seconds} 秒</div>
                {(record.schedule.start_at || record.schedule.end_at) && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {record.schedule.start_at ? `起始: ${formatDateTime(record.schedule.start_at)}` : null}
                    {record.schedule.start_at && record.schedule.end_at ? ' · ' : null}
                    {record.schedule.end_at ? `结束: ${formatDateTime(record.schedule.end_at)}` : null}
                  </div>
                )}
              </div>
            );
          } else if (record.schedule.run_at) {
            return formatDateTime(record.schedule.run_at);
          }
        }
        return '-';
      },
    },
    {
      title: '执行统计',
      key: 'stats',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>总执行: {record.total_run_count || 0}</div>
          <div>成功: {record.total_success_count || 0}</div>
          <div>失败: {record.total_failure_count || 0}</div>
        </div>
      ),
    },
    {
      title: '最后执行',
      dataIndex: 'last_run_at',
      key: 'last_run_at',
      width: 180,
      render: (text) => formatDateTime(text),
    },
    {
      title: '最后状态',
      dataIndex: 'last_run_status',
      key: 'last_run_status',
      width: 100,
      render: (status, record) => {
        const active = !!activeRunsByTaskId?.[record?.task_id];
        if (!status && !active) return '-';
        const key = String(status || '').toLowerCase();
        const statusInfo = runStatusMap[key] || { text: status || '-', color: 'default' };
        return (
          <Space size="small">
            {active ? <Tag color="gold">运行中</Tag> : null}
            {status ? <Tag color={statusInfo.color}>{statusInfo.text}</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => formatDateTime(text),
      sorter: (a, b) => {
        if (!a.created_at || !b.created_at) return 0;
        return new Date(a.created_at) - new Date(b.created_at);
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 500,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => {
              if (isManualTask(record)) {
                handleExecuteOnce(record);
                return;
              }
              handleTriggerOnce(record);
            }}
            disabled={isManualTask(record)
              ? !!activeRunsByTaskId?.[record?.task_id]
              : (String(record?.status || '').toLowerCase() !== 'active' || !!activeRunsByTaskId?.[record?.task_id])}
            style={{ padding: '0 4px' }}
          >
            立即执行
          </Button>
          <Button
            type="link"
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleCancelActiveRun(record)}
            disabled={!activeRunsByTaskId?.[record?.task_id]}
            style={{ padding: '0 4px' }}
          >
            停止运行
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => openRunHistory(record)}
            icon={<HistoryOutlined />}
            style={{ padding: '0 4px' }}
          >
            运行历史
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ padding: '0 4px' }}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleToggleTaskStatus(record)}
            icon={<PoweroffOutlined />}
            style={{ padding: '0 4px' }}
          >
            {String(record?.status || '').toLowerCase() === 'active' ? '停用' : '启用'}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            style={{ padding: '0 4px' }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-shell">
      <Card
        className="page-card"
        bodyStyle={{ padding: 0 }}
      >
        {mountedMainTabs.publish && (
          <div style={{ display: mainTabKey === 'publish' ? 'flex' : 'none', flex: 1, minHeight: 0, flexDirection: 'column' }}>
            <div className="page-panel">
              <div className="page-panel__top">
                <Form
                  form={form}
                  layout="horizontal"
                  onFinish={handleFilterFinish}
                  style={{ marginBottom: 0 }}
                >
                  <Row gutter={[16, 16]} justify="space-between">
                    <Col span={18}>
                      <Row gutter={[16, 16]}>
                        <Col span={6}>
                          <Form.Item label="关键词" name="keyword" style={{ marginBottom: 0 }}>
                            <Input placeholder="搜索任务名称" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="创建者ID" name="creator_user_id" style={{ marginBottom: 0 }}>
                            <Input placeholder="请输入创建者ID" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="创建者" name="creator_user_name" style={{ marginBottom: 0 }}>
                            <Input placeholder="请输入创建者用户名" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="状态" name="status" style={{ marginBottom: 0 }}>
                            <Select
                              mode="multiple"
                              allowClear
                              style={{ width: '100%' }}
                              placeholder="请选择状态"
                              options={[
                                { label: '运行中', value: 'active' },
                                { label: '已停用', value: 'paused' },
                                { label: '已完成', value: 'completed' },
                                { label: '失败', value: 'failed' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="任务类型" name="task_type" style={{ marginBottom: 0 }}>
                            <Select
                              mode="multiple"
                              allowClear
                              style={{ width: '100%' }}
                              placeholder="请选择类型"
                              options={[
                                { label: '定时任务', value: 'cron' },
                                { label: '单次任务', value: 'once' },
                                { label: '间隔任务', value: 'interval' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>

                    <Col span={6} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Space size="small" style={{ width: '100%', justifyContent: 'flex-end' }}>
                        <Button type="primary" style={{ width: 80 }} onClick={handleSearch}>
                          检索
                        </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleCreate}
                    >
                      创建发布任务
                    </Button>
                  </Space>
                </Col>
              </Row>
              </Form>
              </div>

              <div className="page-panel__body">
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  rowKey="task_id"
                  loading={loading}
                  scroll={{ x: 3200 }}
                  rowSelection={{
                    selectedRowKeys: selectedTaskKeys,
                    onChange: (keys) => {
                      setSelectedTaskKeys((prevKeys) => {
                        const currentPageIds = (dataSource || []).map((item) => item.task_id);
                        const otherPageSelected = (prevKeys || []).filter((key) => !currentPageIds.includes(key));
                        const allSelectedKeys = [...otherPageSelected, ...(keys || [])];
                        const uniqueKeys = Array.from(new Set(allSelectedKeys));
                        return uniqueKeys;
                      });
                    },
                  }}
                  pagination={false}
                />
              </div>

              <div className="page-panel__bottom" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px 0',
                background: '#fff',
              }}>
                <div>
                  {selectedTaskKeys.length > 0 && (
                    <Space>
                      <Button onClick={toggleSelectAllCurrentPage}>
                        {isAllCurrentPageSelected ? '取消全选(本页)' : '全选本页'}
                      </Button>
                      <span>已选择 {selectedTaskKeys.length} 项</span>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleBatchDelete}
                      >
                        批量删除
                      </Button>
                      <Button onClick={() => setSelectedTaskKeys([])}>清空全部</Button>
                    </Space>
                  )}
                </div>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  showTotal={(total) => `共 ${total} 条记录`}
                  showSizeChanger
                  showQuickJumper
                  style={{ margin: 0 }}
                  onChange={(page, pageSize) => {
                    const formValues = form.getFieldsValue();
                    const searchParams = buildSearchParams(formValues, page, pageSize);
                    loadData(searchParams, page, pageSize);
                  }}
                  onShowSizeChange={(current, size) => {
                    const formValues = form.getFieldsValue();
                    const searchParams = buildSearchParams(formValues, 1, size);
                    loadData(searchParams, 1, size);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {mountedMainTabs.produce && (
          <div style={{ display: mainTabKey === 'produce' ? 'flex' : 'none', flex: 1, minHeight: 0, flexDirection: 'column' }}>
            <ProducerSchedulesPanel />
          </div>
        )}
      </Card>

      {/* 任务详情抽屉 */}
      <Drawer
        title="任务详情"
        placement="right"
        width={700}
        onClose={() => {
          setDrawerVisible(false);
          setCurrentTask(null);
          detailForm.resetFields();
        }}
        open={drawerVisible}
        loading={detailLoading}
        mask={true}
        maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setDrawerVisible(false)}>取消</Button>
              <Button type="primary" onClick={handleSave}>保存</Button>
            </Space>
          </div>
        }
      >
        {currentTask && (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'preview',
                label: '任务信息',
                children: (
                  <div style={{ padding: '16px 0' }}>
                    <div style={{ marginBottom: 24 }}>
                      <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>基本信息</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>任务ID</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{currentTask.task_id}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>任务类型</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{taskTypeMap[currentTask.task_type] || currentTask.task_type}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>状态</div>
                          <div style={{ fontSize: 14 }}>
                            <Tag color={statusMap[currentTask.status]?.color}>
                              {statusMap[currentTask.status]?.text || currentTask.status}
                            </Tag>
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>创建者ID</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{currentTask.creator_user_id || '-'}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>创建者</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{currentTask.creator_user_name || '-'}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>账号管理员</div>
                          <div style={{ fontSize: 14, color: '#333', wordBreak: 'break-word' }}>
                            {formatListField(currentTask.operator_user_names)}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>管理员ID</div>
                          <div style={{ fontSize: 14, color: '#333', wordBreak: 'break-word' }}>
                            {formatListField(currentTask.operator_user_ids)}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>目标账号</div>
                          <div style={{ fontSize: 14, color: '#333', wordBreak: 'break-word' }}>
                            {formatListField(currentTask.target_account_names)}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>创建时间</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{formatDateTime(currentTask.created_at)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>更新时间</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{formatDateTime(currentTask.updated_at)}</div>
                        </div>
                      </div>
                    </div>
                    {currentTask.schedule && (
                      <div style={{ marginBottom: 24 }}>
                        <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>定时规则</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <div>
                            <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>触发类型</div>
                            <div style={{ fontSize: 14, color: '#333' }}>{currentTask.schedule.trigger_type}</div>
                          </div>
                          {currentTask.schedule.cron_expression && (
                            <div>
                              <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>Cron表达式</div>
                              <div style={{ fontSize: 14, color: '#333' }}>{currentTask.schedule.cron_expression}</div>
                            </div>
                          )}
                          {currentTask.schedule.run_at && (
                            <div>
                              <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>执行时间</div>
                              <div style={{ fontSize: 14, color: '#333' }}>{formatDateTime(currentTask.schedule.run_at)}</div>
                            </div>
                          )}
                          {currentTask.schedule.interval_seconds && (
                            <div>
                              <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>间隔（秒）</div>
                              <div style={{ fontSize: 14, color: '#333' }}>{currentTask.schedule.interval_seconds}</div>
                            </div>
                          )}
                          {currentTask.schedule.start_at && (
                            <div>
                              <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>起始时间</div>
                              <div style={{ fontSize: 14, color: '#333' }}>{formatDateTime(currentTask.schedule.start_at)}</div>
                            </div>
                          )}
                          {currentTask.schedule.end_at && (
                            <div>
                              <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>结束时间</div>
                              <div style={{ fontSize: 14, color: '#333' }}>{formatDateTime(currentTask.schedule.end_at)}</div>
                            </div>
                          )}
                          {currentTask.schedule.timezone && (
                            <div>
                              <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>时区</div>
                              <div style={{ fontSize: 14, color: '#333' }}>{currentTask.schedule.timezone}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {currentTask.filters && (
                      <div style={{ marginBottom: 24 }}>
                        <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>筛选条件</h3>
                        <div style={{ fontSize: 14, color: '#666', lineHeight: 1.8 }}>
                          {JSON.stringify(currentTask.filters, null, 2)}
                        </div>
                      </div>
                    )}
                    {currentTask.target_platforms && currentTask.target_platforms.length > 0 && (
                      <div style={{ marginBottom: 24 }}>
                        <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>目标平台</h3>
                        <Space size={[0, 8]} wrap>
                          {currentTask.target_platforms.map((platform, index) => (
                            <Tag key={index}>{platform}</Tag>
                          ))}
                        </Space>
                      </div>
                    )}
                    <div style={{ marginBottom: 24 }}>
                      <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>Agent执行状态</h3>
                      {(() => {
                        const summary = summarizeTaskAgentStatus(currentTask);
                        const ids = normalizeList(currentTask?.target_account_ids);
                        const names = normalizeList(currentTask?.target_account_names);
                        return (
                          <div>
                            <div style={{ marginBottom: 8 }}>
                              <Tag color={summary.tagColor}>{summary.tagText}</Tag>
                              <span style={{ color: '#333', marginLeft: 8 }}>{summary.detail}</span>
                            </div>
                            {ids.length > 0 ? (
                              <div style={{ display: 'grid', gap: 8 }}>
                                {ids.map((accountId, idx) => {
                                  const account = taskAccountMap?.[accountId];
                                  const view = account ? buildAccountAgentView(account) : {
                                    tagText: taskAccountLoading ? '加载中' : '未知',
                                    tagColor: taskAccountLoading ? 'default' : 'orange',
                                    summary: taskAccountLoading ? '正在加载账号状态' : '未找到账号状态',
                                  };
                                  const mode = String(account?.execution_mode || view.executionMode || '').trim();
                                  const modeInfo = mode ? (executionModeMap[mode] || { text: mode, color: 'default' }) : null;
                                  const accountName = names[idx] || account?.account_name || accountId;
                                  return (
                                    <div key={accountId} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                      <span style={{ color: '#333' }}>{accountName}</span>
                                      <Tag color={view.tagColor}>{view.tagText}</Tag>
                                      {modeInfo ? <Tag color={modeInfo.color}>{modeInfo.text}</Tag> : null}
                                      <span style={{ color: '#999' }}>{view.summary}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ color: '#999' }}>未配置目标账号</div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ marginBottom: 24 }}>
                      <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>执行统计</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>总执行次数</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{currentTask.total_run_count || 0}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>成功次数</div>
                          <div style={{ fontSize: 14, color: '#52c41a' }}>{currentTask.total_success_count || 0}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>失败次数</div>
                          <div style={{ fontSize: 14, color: '#ff4d4f' }}>{currentTask.total_failure_count || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                key: 'edit',
                label: '编辑任务',
                children: (
                  <Form
                    form={detailForm}
                    layout="vertical"
                    style={{ padding: '16px 0' }}
                  >
                    <Form.Item
                      label="任务名称"
                      name="task_name"
                      rules={[{ required: true, message: '请输入任务名称' }]}
                    >
                      <Input placeholder="请输入任务名称" />
                    </Form.Item>

                    <Form.Item
                      label="每账号最多发布条数"
                      name="max_items_per_account_per_run"
                      initialValue={1}
                      rules={[{ required: true, message: '请输入条数' }]}
                    >
                      <InputNumber min={1} max={50} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      label="任务并发数"
                      name="max_concurrency"
                      initialValue={3}
                      rules={[{ required: true, message: '请输入并发数' }]}
                    >
                      <InputNumber min={1} max={10} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      label="状态"
                      name="status"
                      rules={[{ required: true, message: '请选择状态' }]}
                    >
                      <Select placeholder="请选择状态">
                        <Select.Option value="active">运行中</Select.Option>
                        <Select.Option value="paused">已停用</Select.Option>
                        <Select.Option value="completed">已完成</Select.Option>
                        <Select.Option value="failed">失败</Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label="触发类型"
                      name="trigger_type"
                      rules={[{ required: true, message: '请选择触发类型' }]}
                    >
                      <Select placeholder="请选择触发类型">
                        <Select.Option value="cron">定时任务 (Cron)</Select.Option>
                        <Select.Option value="interval">间隔任务</Select.Option>
                        <Select.Option value="once">单次任务</Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) => prevValues.trigger_type !== currentValues.trigger_type}
                    >
                      {({ getFieldValue }) => {
                        const triggerType = getFieldValue('trigger_type');
                        if (triggerType === 'cron') {
                          return (
                            <>
                              <Form.Item
                                label="Cron表达式"
                                name="cron_expression"
                                rules={[{ required: true, message: '请输入Cron表达式' }]}
                              >
                                <Input placeholder="例如: 0 3 * * *" />
                              </Form.Item>
                              <Form.Item
                                label="时区"
                                name="timezone"
                              >
                                <Input placeholder="例如: Asia/Shanghai" />
                              </Form.Item>
                            </>
                          );
                        }

                        if (triggerType === 'interval') {
                          return (
                            <>
                              <Form.Item
                                label="间隔（秒）"
                                name="interval_seconds"
                                rules={[{ required: true, message: '请输入间隔秒数' }]}
                              >
                                <InputNumber min={1} style={{ width: '100%' }} placeholder="例如: 3600（每 1 小时）" />
                              </Form.Item>
                              <Form.Item label="起始时间（可选）" name="start_at">
                                <DatePicker
                                  showTime
                                  format="YYYY-MM-DD HH:mm:ss"
                                  style={{ width: '100%' }}
                                  placeholder="不填则立即生效"
                                />
                              </Form.Item>
                              <Form.Item label="结束时间（可选）" name="end_at">
                                <DatePicker
                                  showTime
                                  format="YYYY-MM-DD HH:mm:ss"
                                  style={{ width: '100%' }}
                                  placeholder="不填则一直执行"
                                />
                              </Form.Item>
                              <Form.Item
                                label="时区"
                                name="timezone"
                              >
                                <Input placeholder="例如: Asia/Shanghai" />
                              </Form.Item>
                            </>
                          );
                        }

                        return (
                          <>
                            <Form.Item
                              label="执行时间"
                              name="run_at"
                              rules={[{ required: true, message: '请选择执行时间' }]}
                            >
                              <DatePicker
                                showTime
                                format="YYYY-MM-DD HH:mm:ss"
                                style={{ width: '100%' }}
                                placeholder="请选择执行时间"
                              />
                            </Form.Item>
                            <Form.Item
                              label="时区"
                              name="timezone"
                            >
                              <Input placeholder="例如: Asia/Shanghai" />
                            </Form.Item>
                          </>
                        );
                      }}
                    </Form.Item>

                    <Form.Item
                      label="内容类型"
                      name="content_types"
                    >
                      <Select mode="multiple" placeholder="请选择内容类型" allowClear>
                        <Select.Option value="视频">视频</Select.Option>
                        <Select.Option value="图文">图文</Select.Option>
                        <Select.Option value="笔记">笔记</Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label="内容来源"
                      name="content_sources"
                    >
                      <Select mode="multiple" placeholder="请选择内容来源" allowClear>
                        <Select.Option value="自建">自建</Select.Option>
                        <Select.Option value="抓取">抓取</Select.Option>
                        <Select.Option value="AI生成">AI生成</Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label="目标平台"
                      name="target_platforms"
                      rules={[{ required: true, message: '请选择目标平台' }]}
                    >
                      <Select mode="multiple" placeholder="请选择目标平台" allowClear>
                        <Select.Option value="微信公众号">微信公众号</Select.Option>
                        <Select.Option value="微信视频号">微信视频号</Select.Option>
                        <Select.Option value="小红书">小红书</Select.Option>
                        <Select.Option value="抖音">抖音</Select.Option>
                        <Select.Option value="快手">快手</Select.Option>
                        <Select.Option value="百家号">百家号</Select.Option>
                        <Select.Option value="知乎">知乎</Select.Option>
                        <Select.Option value="B站">B站</Select.Option>
                        <Select.Option value="今日头条">今日头条</Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label="目标账号"
                      name="target_account_ids"
                    >
                      <Select
                        mode="multiple"
                        placeholder={hasTargetPlatforms ? '请选择目标账号' : '请先选择目标平台'}
                        allowClear
                        showSearch
                        loading={targetAccountLoading}
                        options={targetAccountOptions}
                        optionFilterProp="label"
                        disabled={!hasTargetPlatforms}
                      />
                    </Form.Item>
                  </Form>
                ),
              },
            ]}
          />
        )}
      </Drawer>

      {/* 创建发布任务-选择筛选条件抽屉 */}
      <Drawer
        title="创建发布任务-选择筛选条件"
        placement="right"
        width={800}
        onClose={() => {
          setCreateTaskDrawerVisible(false);
          filterForm.resetFields();
        }}
        open={createTaskDrawerVisible}
        mask={true}
        maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setCreateTaskDrawerVisible(false);
                filterForm.resetFields();
              }}>取消</Button>
              <Button type="primary" onClick={() => filterForm.submit()}>下一步</Button>
            </Space>
          </div>
        }
      >
        <Form
          form={filterForm}
          layout="vertical"
          onFinish={handleFilterFormFinish}
          style={{ padding: '16px 0' }}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item label="内容来源" name="source">
                <Select
                  style={{ width: '100%' }}
                  mode="multiple"
                  allowClear
                  placeholder="请选择内容来源"
                  maxTagCount="responsive"
                  options={[
                    { label: '自建', value: '自建' },
                    { label: '抓取', value: '抓取' },
                    { label: 'AI生成', value: 'AI生成' },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="标签"
                name="tags"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      if (value.includes('，')) {
                        return Promise.reject(new Error('标签分隔符请使用英文逗号","，不能使用中文逗号"，"'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="多个标签用逗号分隔，例如：tag1,tag2" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="是否需要人工审核" name="manual_review_required">
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  placeholder="请选择是否需要人工审核"
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="是否审核" name="is_manual_reviewed">
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  placeholder="请选择是否审核"
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="标题" name="title">
                <Input placeholder="请输入标题" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="创作者" name="creator_user_id">
                <Input placeholder="请输入创作者ID" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="风险状态" name="risk_flag">
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  placeholder="请选择风险状态"
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="内容状态" name="status">
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="请选择内容状态"
                  options={[
                    { label: '待发布', value: '待发布' },
                    { label: '已发布', value: '已发布' },
                    { label: '作废', value: '作废' },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="内容ID" name="content_id">
                <Input placeholder="请输入内容ID" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="关键词" name="keyword">
                <Input placeholder="请输入关键词" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="投递平台" name="available_platforms">
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="请选择投递平台"
                  options={[
                    { label: '微信公众号', value: '微信公众号' },
                    { label: '微信视频号', value: '微信视频号' },
                    { label: '小红书', value: '小红书' },
                    { label: '抖音', value: '抖音' },
                    { label: '快手', value: '快手' },
                    { label: '百家号', value: '百家号' },
                    { label: '知乎', value: '知乎' },
                    { label: 'B站', value: 'B站' },
                    { label: '今日头条', value: '今日头条' },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="内容类型" name="content_type">
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="请选择内容类型"
                  options={[
                    { label: '视频', value: '视频' },
                    { label: '图文', value: '图文' },
                    { label: '笔记', value: '笔记' },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="入库时间" name="ingested_at_range">
                <RangePicker
                  style={{ width: '100%' }}
                  showTime={{ format: 'HH:mm:ss' }}
                  format="YYYY-MM-DD HH:mm:ss"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>

      {/* 创建发布任务-选择发布账号抽屉 */}
      <Drawer
        title="创建发布任务-选择发布账号"
        placement="right"
        width={800}
        onClose={() => {
          setPublishAccountDrawerVisible(false);
          setAccountDataSource([]);
          setSelectedAccountKeys([]);
          setSelectedAccounts([]);
          selectedAccountMapRef.current.clear();
          accountFilterForm.resetFields();
          setAccountPagination({
            current: 1,
            pageSize: 50,
            total: 0,
          });
        }}
        open={publishAccountDrawerVisible}
        loading={accountLoading}
        mask={true}
        maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToFilterSelection}
            >
              返回
            </Button>
            <Space>
              <Button onClick={() => {
                setPublishAccountDrawerVisible(false);
                setSelectedAccountKeys([]);
                setSelectedAccounts([]);
                selectedAccountMapRef.current.clear();
              }}>
                取消
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmAccountSelection}
                disabled={selectedAccountKeys.length === 0}
              >
                确认选择 ({selectedAccountKeys.length})
              </Button>
            </Space>
          </div>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* 筛选表单 */}
          <Form
            form={accountFilterForm}
            layout="inline"
            onFinish={handleAccountFilter}
            style={{ marginBottom: 16 }}
          >
            <Form.Item label="平台" name="platforms">
              <Select
                mode="multiple"
                allowClear
                style={{ width: 200 }}
                placeholder="请选择平台"
                options={[
                  { label: '微信公众号', value: '微信公众号' },
                  { label: '微信视频号', value: '微信视频号' },
                  { label: '小红书', value: '小红书' },
                  { label: '抖音', value: '抖音' },
                  { label: '快手', value: '快手' },
                  { label: '百家号', value: '百家号' },
                  { label: '知乎', value: '知乎' },
                  { label: 'B站', value: 'B站' },
                  { label: '今日头条', value: '今日头条' },
                ]}
              />
            </Form.Item>
            <Form.Item label="状态" name="status">
              <Select
                mode="multiple"
                allowClear
                style={{ width: 200 }}
                placeholder="请选择状态"
                options={[
                  { label: '活跃', value: 'active' },
                  { label: '未激活', value: 'inactive' },
                  { label: '已暂停', value: 'suspended' },
                ]}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                筛选
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={() => {
                  accountFilterForm.resetFields();
                  handleAccountFilter();
                }}
              >
                重置
              </Button>
            </Form.Item>
          </Form>
          {selectedAccountAgentRisk && selectedAccountAgentRisk.localRequired > 0 && (
            <Alert
              type={(selectedAccountAgentRisk.unbound + selectedAccountAgentRisk.offline) > 0 ? 'error' : 'warning'}
              showIcon
              style={{ marginBottom: 12 }}
              message="已选账号 Agent 依赖检查"
              description={`共选 ${selectedAccountAgentRisk.total} 个账号；依赖 Agent ${selectedAccountAgentRisk.localRequired} 个，其中就绪 ${selectedAccountAgentRisk.ready}，未绑定 ${selectedAccountAgentRisk.unbound}，离线 ${selectedAccountAgentRisk.offline}，待登录 ${selectedAccountAgentRisk.needsLogin}${selectedAccountAgentRisk.unknown ? `，待加载 ${selectedAccountAgentRisk.unknown}` : ''}。`}
            />
          )}
          <Table
            className="account-select-table"
            size="small"
            columns={[
              {
                title: '平台',
                dataIndex: 'platform',
                key: 'platform',
                width: 90,
                render: (platform) => {
                  const platformMap = {
                    'xhs': '小红书',
                    'douyin': '抖音',
                    'wechat': '微信公众号',
                    'wechat_channels': '微信视频号',
                    'weixin_channels': '微信视频号',
                    'kuaishou': '快手',
                    'baijiahao': '百家号',
                    'zhihu': '知乎',
                    'bilibili': 'B站',
                    'toutiao': '今日头条',
                  };
                  return platformMap[platform] || platform;
                },
              },
              {
                title: '账号管理员',
                dataIndex: 'operator_user_names',
                key: 'operator_user_id',
                width: 140,
                ellipsis: true,
                render: (operator_user_names) => {
                  if (Array.isArray(operator_user_names)) {
                    const text = operator_user_names.join(', ');
                    return <span title={text}>{text}</span>;
                  }
                  return <span title={operator_user_names || '-'}>{operator_user_names || '-'}</span>;
                },
              },
              {
                title: '账号名',
                dataIndex: 'account_name',
                key: 'account_name',
                width: 180,
                ellipsis: true,
                render: (account_name) => <span title={account_name || '-'}>{account_name || '-'}</span>,
              },
              {
                title: '账号id',
                dataIndex: 'account_id',
                key: 'account_id',
                width: 140,
                ellipsis: true,
                render: (account_id) => <span title={account_id || '-'}>{account_id || '-'}</span>,
              },
              {
                title: '执行模式',
                dataIndex: 'execution_mode',
                key: 'execution_mode',
                width: 120,
                render: (value, record) => {
                  const mode = String(value || record?.login_readiness?.execution_mode || '').trim();
                  if (!mode) return '-';
                  const info = executionModeMap[mode] || { text: mode, color: 'default' };
                  return <Tag color={info.color}>{info.text}</Tag>;
                },
              },
              {
                title: '登录就绪',
                key: 'login_readiness',
                width: 180,
                render: (_, record) => {
                  const view = buildAccountAgentView(record);
                  return (
                    <div>
                      <Tag color={view.tagColor}>{view.tagText}</Tag>
                      <div style={{ color: '#666', fontSize: 12, lineHeight: 1.3 }}>
                        {view.summary}
                      </div>
                    </div>
                  );
                },
              },
              {
                title: '权限',
                dataIndex: 'permission',
                key: 'permission',
                width: 110,
                render: (permission) => (permission?.can_operate === false ? '仅可查看' : '可操作'),
              },
              {
                title: '检测',
                key: 'account_check',
                width: 220,
                render: (_, record) => {
                  const canOperate = record?.permission?.can_operate !== false;
                  return (
                    <Space size="small">
                      <DouyinAccountCheckButton
                        account={record}
                        label="检测"
                        buttonProps={{ style: { padding: '0 4px' }, disabled: !canOperate }}
                      />
                      <KuaishouAccountCheckButton
                        account={record}
                        label="检测"
                        buttonProps={{ style: { padding: '0 4px' }, disabled: !canOperate }}
                      />
                      <XhsAccountCheckButton
                        account={record}
                        label="检测"
                        restartMcp
                        buttonProps={{ style: { padding: '0 4px' }, disabled: !canOperate }}
                      />
                    </Space>
                  );
                },
              },
            ]}
            dataSource={accountDataSource}
            rowKey="account_id"
            loading={accountLoading}
            rowSelection={{
              selectedRowKeys: selectedAccountKeys,
              preserveSelectedRowKeys: true,
              getCheckboxProps: (record) => ({
                disabled: record?.permission?.can_operate === false,
              }),
              onSelect: (record, selected) => {
                if (!record?.account_id) return;
                if (selected) {
                  selectedAccountMapRef.current.set(record.account_id, record);
                } else {
                  selectedAccountMapRef.current.delete(record.account_id);
                }
              },
              onSelectAll: (selected, selectedRows, changeRows) => {
                const rows = Array.isArray(changeRows) ? changeRows : [];
                if (selected) {
                  rows.forEach((row) => {
                    if (row?.account_id) selectedAccountMapRef.current.set(row.account_id, row);
                  });
                } else {
                  rows.forEach((row) => {
                    if (row?.account_id) selectedAccountMapRef.current.delete(row.account_id);
                  });
                }
              },
              onChange: (selectedRowKeys) => {
                // AntD 在分页/远端数据场景下可能只回传“当前页”的 keys，这里做一次合并，确保跨页选择不丢失
                setSelectedAccountKeys((prevKeys) => {
                  const currentPageIds = (accountDataSource || []).map((a) => a.account_id);
                  const keepFromOtherPages = (prevKeys || []).filter((key) => !currentPageIds.includes(key));
                  const merged = Array.from(new Set([...keepFromOtherPages, ...(selectedRowKeys || [])]));
                  return merged;
                });
              },
            }}
            pagination={{
              current: accountPagination.current,
              pageSize: accountPagination.pageSize,
              total: accountPagination.total,
              showTotal: (total) => `共 ${total} 条记录`,
              showSizeChanger: true,
              showQuickJumper: true,
              onChange: (page, pageSize) => {
                setAccountPagination({
                  ...accountPagination,
                  current: page,
                  pageSize: pageSize,
                });
                loadPublishAccounts({ page, page_size: pageSize });
              },
              onShowSizeChange: (current, size) => {
                setAccountPagination({
                  ...accountPagination,
                  current: 1,
                  pageSize: size,
                });
                loadPublishAccounts({ page: 1, page_size: size });
              },
            }}
            scroll={{ x: 'max-content' }}
          />
        </Space>
      </Drawer>

      {/* 创建发布任务-选择发布时间抽屉 */}
      <Drawer
        title="创建发布任务-选择发布时间"
        placement="right"
        width={800}
        onClose={() => {
          setPublishTimeDrawerVisible(false);
          publishTimeForm.resetFields();
          setScheduleParseResult(null);
        }}
        open={publishTimeDrawerVisible}
        mask={true}
        maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToAccountSelection}
            >
              返回
            </Button>
            <Space>
              <Button onClick={() => {
                setPublishTimeDrawerVisible(false);
                publishTimeForm.resetFields();
                setScheduleParseResult(null);
              }}>
                取消
              </Button>
              <Button
                type="primary"
                onClick={handleCreateTask}
                disabled={!scheduleParseResult || !scheduleParseResult.success}
              >
                创建任务
              </Button>
            </Space>
          </div>
        }
      >
        <Form
          form={publishTimeForm}
          layout="vertical"
          style={{ padding: '16px 0' }}
        >
          <Form.Item
            label="定时任务名称"
            name="task_name"
            rules={[{ required: true, message: '请输入定时任务名称' }]}
          >
            <Input placeholder="请输入定时任务名称" />
          </Form.Item>

          <Form.Item
            label={selectedAccounts?.length === 1 ? '本次最多发布条数' : '每账号最多发布条数'}
            name="max_items_per_account_per_run"
            initialValue={1}
            rules={[{ required: true, message: '请输入条数' }]}
          >
            <InputNumber min={1} max={50} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="任务并发数"
            name="max_concurrency"
            initialValue={2}
            rules={[{ required: true, message: '请输入并发数' }]}
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="发布时间"
            name="schedule_text"
            rules={[{ required: true, message: '请输入发布时间文本' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder={[
                '示例（Cron）：每周一到周五 9:00 执行',
                '示例（一次）：2026-01-15 14:30 执行一次',
                '示例（间隔）：从现在开始每 2 小时执行一次，持续 3 天',
              ].join('\n')}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              onClick={handleParseSchedule}
              loading={parsingSchedule}
            >
              文本解析
            </Button>
          </Form.Item>

          {parsingSchedule && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>正在解析...</div>
            </div>
          )}

          {scheduleParseResult && !parsingSchedule && (
            <div style={{ marginTop: 16 }}>
              {scheduleParseResult.success ? (
                <div>
                  <Alert
                    message="解析成功"
                    description="定时任务文本解析成功，请确认以下信息："
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  
                  {scheduleParseResult.data.schedule && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>定时规则：</div>
                      <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, marginBottom: 8 }}>
                        <div>
                          触发类型：
                          {scheduleParseResult.data.schedule.trigger_type === 'cron'
                            ? '定时任务 (Cron)'
                            : scheduleParseResult.data.schedule.trigger_type === 'interval'
                              ? '间隔任务 (Interval)'
                              : '单次任务'}
                        </div>
                        {scheduleParseResult.data.schedule.cron_expression && (
                          <div>Cron表达式：{scheduleParseResult.data.schedule.cron_expression}</div>
                        )}
                        {scheduleParseResult.data.schedule.run_at && (
                          <div>执行时间：{scheduleParseResult.data.schedule.run_at}</div>
                        )}
                        {scheduleParseResult.data.schedule.interval_seconds && (
                          <div>间隔（秒）：{scheduleParseResult.data.schedule.interval_seconds}</div>
                        )}
                        {(scheduleParseResult.data.schedule.start_at || scheduleParseResult.data.schedule.end_at) && (
                          <div>
                            区间：
                            {scheduleParseResult.data.schedule.start_at ? `起始 ${scheduleParseResult.data.schedule.start_at}` : '起始不限'}
                            {' ～ '}
                            {scheduleParseResult.data.schedule.end_at ? `结束 ${scheduleParseResult.data.schedule.end_at}` : '结束不限'}
                          </div>
                        )}
                        <div>时区：{scheduleParseResult.data.schedule.timezone || 'Asia/Shanghai'}</div>
                      </div>
                    </div>
                  )}

                  {scheduleParseResult.data.next_runs_display && scheduleParseResult.data.next_runs_display.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>后续执行时间：</div>
                      <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                        {scheduleParseResult.data.next_runs_display.map((time, index) => (
                          <div key={index} style={{ marginBottom: 4 }}>
                            {index + 1}. {time}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {scheduleParseResult.data.warnings && scheduleParseResult.data.warnings.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <Alert
                        message="警告"
                        description={
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {scheduleParseResult.data.warnings.map((warning, index) => (
                              <li key={index}>{warning}</li>
                            ))}
                          </ul>
                        }
                        type="warning"
                        showIcon
                      />
                    </div>
                  )}
                </div>
              ) : scheduleParseResult.needsClarification ? (
                <div>
                  <Alert
                    message="需要补充信息"
                    description="请根据以下问题补充定时任务信息："
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  
                  {scheduleParseResult.data.questions && scheduleParseResult.data.questions.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>需要回答的问题：</div>
                      <div style={{ background: '#fff7e6', padding: 12, borderRadius: 4, border: '1px solid #ffd591' }}>
                        {scheduleParseResult.data.questions.map((question, index) => (
                          <div key={index} style={{ marginBottom: 8, fontWeight: 500 }}>
                            {index + 1}. {question}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Alert
                  message="解析失败"
                  description={scheduleParseResult.error || '无法解析定时任务文本，请检查输入格式'}
                  type="error"
                  showIcon
                />
              )}
            </div>
          )}
        </Form>
      </Drawer>

      <PublishRunViewer
        open={publishRunViewerOpen}
        taskRunId={activeTaskRunId}
        onClose={() => setPublishRunViewerOpen(false)}
      />

      <PublishRunHistoryDrawer
        open={publishRunHistoryOpen}
        task={historyTask}
        onClose={() => setPublishRunHistoryOpen(false)}
      />
    </div>
  );
};

export default TimedPublish;
